import type { Claim } from '@/types/claim'
import type { ContractStatus } from '@/types/contractStatuses'
import type { BelongingCovers } from '@/types/covers'
import type { Document } from '@/types/documents'
import type { EuroCents } from '@/types/general'
import type { LifeProductType, ProductType } from '@/types/productTypes'

interface BaseContract {
  uuid: string
  status: ContractStatus
  productShortLabel: {
    fr: string
    nl: string
    en: string
  }
  reference: string
  insurer: { name: string; logoAssetKey: string }
  payment: {
    premium: EuroCents
    splitting: Splitting
    nextDueDate: Date
  }
  policyOwner: {
    firstName: string
    lastName: string
    vatNumber?: string
  }
  productReference: string
  productLineId: string
  covers: BelongingCovers[]
  missingDocumentsCount: number
  pendingSignature: boolean
  amendmentPendingSignature: boolean
  couplingRole: 'primary' | 'beneficiary' | null
  contractType: string
  documents: Document[]
  aliases: string[]
  baloisePack: Boolean
  chargebeePortalLink?: string
  unpaidPending?: UnpaidInformations | null
  contractRequestUuid: string
  insurerPhoneNumber: string
  claimsLink: {
    fr: string
    nl: string
  }
  startDate?: string
  endDate?: string
  greenCardDocuments?: Document[]
  stoppingDate: Date
  stoppingReason?: StoppingReason
  fullPackageOfferPrice?: null
  claims: Claim[]
}

interface VehicleContract extends BaseContract {
  brand: string
  model: string
  licensePlate: string
}

interface CarContract extends VehicleContract {
  productType: ProductType.Car
}

interface MotorbikeContract extends VehicleContract {
  productType: ProductType.Motorbike
  motorbikeBelongingUuid: string
}

interface BikeContract extends BaseContract {
  productType: ProductType.Bicycle
  brand: string
}

interface HouseContract extends BaseContract {
  productType: ProductType.House
  address: Address
}

interface LifeContract extends BaseContract {
  productType: LifeProductType
}

//* Contract
export type Contract = CarContract | MotorbikeContract | HouseContract | BikeContract | LifeContract

//* UnPaid
export interface UnpaidInformations {
  amountDue: number
  communication: string
  dueAt: string
  iban: string
}

export interface UnPaidItem extends UnpaidInformations {
  contract: Contract
}

//* Address
export interface Address {
  street: string
  number: string
  box?: string
  postalCode: string
  city: string
}

//* Payment
export enum Splitting {
  Yearly = 'yearly',
  Semiannual = 'semiannual',
  Quarterly = 'quarterly',
  Monthly = 'monthly',
  Unknown = 'unknown'
}

export enum StoppingReason {
  paid_up_following_insurer_change = 'paid_up_following_insurer_change',
  paid_up_following_tax_status_change = 'paid_up_following_tax_status_change',
  paid_up_following_unpaid_premiums = 'paid_up_following_unpaid_premiums',
  paid_up_following_80_percent_rule_compliance = 'paid_up_following_80_percent_rule_compliance',
  paid_up_following_policy_owner_request = 'paid_up_following_policy_owner_request',
  rewrite = 'rewrite',
  already_terminated_contract_transferred = 'already_terminated_contract_transferred',
  policy_owner_death = 'policy_owner_death',
  mistake = 'mistake',
  surrender = 'surrender',
  withdrawal_right = 'withdrawal_right',
  natural_expiry = 'natural_expiry',
  unfavorable_transfer = 'unfavorable_transfer'
}

export const PaidUpStoppingReason = [
  StoppingReason.paid_up_following_80_percent_rule_compliance,
  StoppingReason.paid_up_following_insurer_change,
  StoppingReason.paid_up_following_policy_owner_request,
  StoppingReason.paid_up_following_tax_status_change,
  StoppingReason.paid_up_following_unpaid_premiums
]
